/**
 * Action permissions for the shipment detail view.
 *
 * The permission set is read on every render (`can`, `canAny`, `canAction`, `hasPerm`),
 * so whatever the /api/shipment/permissions/actions response leaves behind has to stay a
 * Set. Storing an array there — as the failure path used to — turns the next render into
 * "allowedActions.has is not a function" and takes the whole detail view down, which is
 * what clients saw as a white screen that only loaded back after a few refreshes.
 */

/** Case/separator-insensitive form of an action name: "Action/Assign_Carrier" -> "ACTION ASSIGN CARRIER". */
export const normalizeActionName = (s) =>
  (s ?? "")
    .toString()
    .toUpperCase()
    .replace(/[\s/_-]+/g, " ") // collapse hyphen/underscore/slash to space
    .replace(/\s+/g, " ")
    .trim();

/**
 * Build the permission Set from a permissions response body. Accepts either a bare array
 * or an `{ actions: [...] }` envelope; anything else (error envelope, HTML error page,
 * null) yields an empty Set rather than throwing, so a bad payload only hides the action
 * buttons.
 */
export const toAllowedActionsSet = (payload) => {
  const actions = Array.isArray(payload?.actions) ? payload.actions : payload;
  const names = (Array.isArray(actions) ? actions : [])
    .map(normalizeActionName)
    .filter(Boolean); // a null/blank entry would otherwise make can("") true
  return new Set(names);
};

/** Never throws: a non-Set value degrades to "no permission" instead of crashing the view. */
export const hasAllowedAction = (allowedActions, name) =>
  !!allowedActions?.has?.(normalizeActionName(name));
